import { useState } from 'react';
import { themeColor } from './colorThemes';
import CompareLandingPagesModal from './CompareLandingPagesModal';
import { PagesFilterDropdown } from './TopPagesVisitedTable';

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatPct(val) {
  return `${(val * 100).toFixed(1)}%`;
}

export default function OrganicLandingPagesTable({
  pages,
  themeKey,
  siteId,
  excludedPages = [],
  onExclude,
  onRestore,
}) {
  const [compareOpen, setCompareOpen] = useState(false);
  const canExclude = typeof onExclude === 'function' && typeof onRestore === 'function';

  const Header = () => (
    <div className="flex items-center justify-between gap-3 mb-3">
      <h4 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline-variant flex items-center gap-2">
        <svg className="w-4 h-4 text-brand-outline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Organic Landing Pages
      </h4>
      <div className="flex items-center gap-2 flex-shrink-0">
        {siteId && (
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-brand-outline-variant dark:border-brand-outline text-brand-on-surface-variant dark:text-brand-outline hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface transition-colors flex items-center gap-1.5 font-label flex-shrink-0"
            title="Compare against a past period"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            Compare
          </button>
        )}
        {canExclude && (
          <PagesFilterDropdown
            visiblePages={pages || []}
            excludedPages={excludedPages}
            onExclude={onExclude}
            onRestore={onRestore}
            onClearAll={() => excludedPages.forEach((p) => onRestore(p))}
          />
        )}
      </div>
    </div>
  );

  if (!pages || pages.length === 0) {
    return (
      <div>
        <Header />
        <p className="text-sm text-brand-outline dark:text-brand-on-surface-variant text-center py-4">
          {excludedPages.length > 0 ? 'All landing pages are excluded.' : 'No landing page data available.'}
        </p>
        {siteId && (
          <CompareLandingPagesModal
            isOpen={compareOpen}
            onClose={() => setCompareOpen(false)}
            siteId={siteId}
            currentPages={pages || []}
            currentLabel="Current Period"
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="overflow-x-auto rounded-lg border border-brand-outline-variant dark:border-brand-outline">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-surface-container-low dark:bg-brand-on-surface/50">
              <th className="text-left py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">#</th>
              <th className="text-left py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Page</th>
              <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Sessions</th>
              <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Bounce</th>
              <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Engagement</th>
              <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Avg Time</th>
              <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Conversions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((row, i) => (
              <tr key={i} className="border-t border-gray-50 dark:border-brand-outline hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface/30 transition-colors">
                <td className="py-2 px-3 text-brand-outline dark:text-brand-on-surface-variant tabular-nums">{i + 1}</td>
                <td className="py-2 px-3 max-w-[280px]">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: themeColor(themeKey, i % 4) }}
                    />
                    <span className="text-brand-on-surface dark:text-brand-outline-variant  text-xs truncate font-label" title={row.page}>
                      {row.page}
                    </span>
                  </div>
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold" style={{ color: themeColor(themeKey, 0) }}>
                  {row.sessions.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right text-brand-on-surface-variant dark:text-brand-outline tabular-nums">
                  {formatPct(row.bounceRate)}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  <span className={row.engagementRate >= 0.6 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : row.engagementRate >= 0.4 ? 'text-amber-600 dark:text-amber-400' : 'text-brand-on-surface-variant dark:text-brand-outline'}>
                    {formatPct(row.engagementRate)}
                  </span>
                </td>
                <td className="py-2 px-3 text-right text-brand-on-surface-variant dark:text-brand-outline tabular-nums">
                  {formatDuration(row.avgDuration)}
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold text-brand-on-surface dark:text-white">
                  {row.conversions}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {siteId && (
        <CompareLandingPagesModal
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
          siteId={siteId}
          currentPages={pages}
          currentLabel="Current Period"
        />
      )}
    </div>
  );
}
